import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal } from 'antd';
import clsx from 'clsx';
import React, { FC, memo, PropsWithChildren, ReactNode, useCallback } from 'react';
import { FnButtonTypes, FnButtonVariants } from '../../elements/FnButton/constants';
import FnFooterModal from './components/Footer';
import FnTitleModal from './components/Title';
import { FnAntModalWidth, Variants } from './constants';
import { DivContentWrapper } from './styles';

type TProps = {
  isOpen: boolean;
  titleText?: string;
  titleCount?: string;
  titleErrorIcon?: boolean;
  titleImageExistsUri?: string;
  titleSecondLineLabel?: string;
  titleSecondLineValue?: string | JSX.Element;
  titleActionButton?: ReactNode;
  width?: FnAntModalWidth;
  variant?: Variants;
  disableClose?: boolean;
  onClose?: () => void;
  okText?: string;
  okViewType?: FnButtonTypes;
  okVariant?: FnButtonVariants;
  okDisabled?: boolean;
  onOk?: () => void;
  cancelText?: string;
  cancelViewType?: FnButtonTypes;
  cancelVariant?: FnButtonVariants;
  cancelDisabled?: boolean;
  onCancel?: () => void;
  footerShadow?: boolean;
  footerCentered?: boolean;
  noOverlayHeader?: boolean;
  wrapClassName?: string;
  hasFilter?: boolean;
  noPadding?: boolean;
  noTitleLine?: boolean;
  notificationDrawer?: boolean;
  zIndex?: number;
  className?: string;
  guidance?: string;
};

const FnAntModal: FC<PropsWithChildren<TProps>> = ({
  isOpen,
  titleText = '',
  titleCount = '',
  titleErrorIcon = false,
  titleImageExistsUri = '',
  titleSecondLineLabel = '',
  titleSecondLineValue = '',
  titleActionButton = null,
  width = FnAntModalWidth.Lg,
  variant = Variants.Primary,
  disableClose,
  onClose,
  okText = '',
  okViewType = FnButtonTypes.Default,
  okVariant = FnButtonVariants.Primary,
  okDisabled = false,
  onOk = null,
  cancelText = '',
  cancelViewType = FnButtonTypes.Text,
  cancelVariant = FnButtonVariants.Secondary,
  cancelDisabled = false,
  onCancel = null,
  footerShadow = false,
  footerCentered = false,
  hasFilter = false,
  noPadding = false,
  noTitleLine = false,
  notificationDrawer = false,
  zIndex = 1015,
  className = '',
  noOverlayHeader = false,
  wrapClassName = '',
  children,
  guidance = ''
}) => {
  const currentZIndex = noOverlayHeader ? 1011 : zIndex;

  const handleClose = useCallback(() => {
    if (disableClose || !onClose) {
      return;
    }

    onClose();
  }, [onClose]);
  const hasFooter = (okText && onOk) || (cancelText && onCancel);

  return (
    <Modal
      className={clsx(
        {
          ['footer-shadow']: footerShadow,
          ['no-title-line']: noTitleLine,
          ['body-relative']: hasFilter
        },
        variant,
        className
      )}
      visible={isOpen}
      width={width}
      closable={!disableClose}
      wrapClassName={clsx(
        {
          ['no-overlay-header']: noOverlayHeader
        },
        wrapClassName
      )}
      closeIcon={<FontAwesomeIcon icon={faTimes} onClick={handleClose} />}
      zIndex={currentZIndex}
      title={
        titleText ? (
          <FnTitleModal
            text={titleText}
            count={titleCount}
            icon={titleErrorIcon}
            imageExistsUri={titleImageExistsUri}
            secondLineLabel={titleSecondLineLabel}
            secondLineValue={titleSecondLineValue}
            actionBtn={titleActionButton}
          />
        ) : null
      }
      footer={
        hasFooter ? (
          <FnFooterModal
            okText={okText}
            okViewType={okViewType}
            okVariant={okVariant}
            okDisabled={okDisabled}
            onOk={onOk}
            cancelText={cancelText}
            cancelViewType={cancelViewType}
            cancelVariant={cancelVariant}
            cancelDisabled={cancelDisabled}
            onCancel={onCancel}
            footerCentered={footerCentered}
            guidance={guidance}
          />
        ) : null
      }
    >
      <DivContentWrapper
        className={clsx('styled-scroll', {
          ['has-footer']: hasFooter,
          ['has-filter']: hasFilter,
          ['no-padding']: noPadding,
          ['notification-drawer']: notificationDrawer
        })}
      >
        {children}
      </DivContentWrapper>
    </Modal>
  );
};

export default memo(FnAntModal);
